// ---------------------------------------------------------------------------
// Registering the service worker, and offering the update.
//
// Two decisions worth stating.
//
// The worker is registered AFTER the first paint. Registering it during
// start-up competes with the very files it is caching, which makes the first
// load of the till slower in exchange for making the second one work offline.
// The till's opening speed is the thing this project has protected all along.
//
// An update is OFFERED, never applied. A new version reaching the browser
// while a cashier has a half-rung order on screen must not reload the page.
// The prompt sits there until somebody is between customers.
// ---------------------------------------------------------------------------

type UpdateHandler = () => void;

let waitingWorker: ServiceWorker | null = null;
let onUpdateAvailable: UpdateHandler | null = null;

/// Whether this browser can run a service worker at all.
///
/// It cannot over plain HTTP other than on localhost, which is exactly the
/// setup a shop might have on an internal network. The application must still
/// work in that case - just without the offline shell - so nothing here
/// throws.
export function serviceWorkerSupported(): boolean {
  return typeof navigator !== 'undefined' && 'serviceWorker' in navigator;
}

export function registerServiceWorker(handler?: UpdateHandler): void {
  if (!serviceWorkerSupported()) return;
  // A dev build has no worker, and registering one would cache a development
  // bundle - the most confusing thing that can happen to somebody editing a
  // component and seeing no change.
  if (import.meta.env.DEV) return;

  onUpdateAvailable = handler ?? null;

  // THE BUG THIS GUARDS AGAINST: this used to be `window.addEventListener('load', ...)`
  // unconditionally. Registration is called from a React effect, and on a
  // fast localhost load the `load` event has often ALREADY FIRED by the time
  // that effect runs - a listener added after the event waits forever, and
  // the worker silently never registered. Found on the real production
  // runtime, not by a unit test: the page was fine, installability was not.
  const whenLoaded = (fn: () => void) => {
    if (document.readyState === 'complete') fn();
    else window.addEventListener('load', fn, { once: true });
  };

  whenLoaded(() => {
    void navigator.serviceWorker
      .register('/sw.js', { scope: '/' })
      .then((registration) => {
        // Already one waiting from a previous visit.
        if (registration.waiting) {
          waitingWorker = registration.waiting;
          onUpdateAvailable?.();
        }

        registration.addEventListener('updatefound', () => {
          const installing = registration.installing;
          if (!installing) return;

          installing.addEventListener('statechange', () => {
            // `controller` is null on the very first install, which is not an
            // update - there is nothing to replace and nothing to ask about.
            if (installing.state === 'installed' && navigator.serviceWorker.controller) {
              waitingWorker = installing;
              onUpdateAvailable?.();
            }
          });
        });
      })
      .catch(() => {
        // No worker means no offline shell. The till still sells online, so
        // this is not worth interrupting anybody over.
      });
  });
}

/// Install the waiting version and reload.
///
/// Only ever called from a button somebody pressed.
export function applyUpdate(): void {
  if (!waitingWorker) {
    window.location.reload();
    return;
  }

  // The worker installs itself, then the controllerchange event tells us it
  // has taken over and the page can safely reload into the new version.
  navigator.serviceWorker.addEventListener('controllerchange', () => window.location.reload(), {
    once: true,
  });

  waitingWorker.postMessage({ type: 'SKIP_WAITING' });
}

/// Whether an update is sitting there waiting to be applied.
export function updateWaiting(): boolean {
  return waitingWorker !== null;
}
